import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from 'react-hot-toast';
import {
  useTheme,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  ArrowBackIosNew,
  Fingerprint,
  WifiOff,
} from "@mui/icons-material";
import { startAuthentication } from '@simplewebauthn/browser';
import { Formik } from "formik";
import * as yup from "yup";
import { setLogin, setCrewRanks, setOfflineLogin } from "state";
import { saveOfflineCredential, getOfflineCredential, verifyOfflinePin, refreshOfflineCredential, saveBiometricCredentialId } from "state/offlineAuth";
import { API_URL } from 'state/constants';
import api from 'state/axios';
import Dropzone from "./Dropzone";


/* ===== PROVIDER LOGOS ===== */
const MicrosoftLogo = () => (
  <svg width="18" height="18" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
    <path fill="#f35325" d="M1 1h10v10H1z"/>
    <path fill="#81bc06" d="M12 1h10v10H12z"/>
    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
    <path fill="#ffba08" d="M12 12h10v10H12z"/>
  </svg>
);

/* ===== MODULE-LEVEL CONSTANTS ===== */
const PROVIDERS = [
  {
    id: 'outlook',
    name: 'Sign in with Microsoft',
    icon: <MicrosoftLogo />,
  },
];

const WEBAUTHN_SUPPORTED = typeof window !== 'undefined' && !!window.PublicKeyCredential;

const validationSchemas = {
  register: yup.object({
    firstName: yup.string().required("required"),
    lastName:  yup.string().required("required"),
    email:     yup.string().email("invalid email").required("required"),
    password:  yup.string().required("required"),
  }),
  login: yup.object({
    email:    yup.string().email("invalid email").required("required"),
    password: yup.string().required("required"),
  }),
  forgotpassword: yup.object({
    email: yup.string().email("Invalid email").required("Required"),
  }),
  resetpassword: yup.object({
    password:        yup.string().required("Required"),
    confirmPassword: yup.string()
      .oneOf([yup.ref('password')], "Passwords must match")
      .required("Required"),
  }),
};

const SUBMIT_LABELS = {
  login:          "Login",
  register:       "Register",
  forgotpassword: "Submit",
  resetpassword:  "Reset",
};

const initialValues = {
  register:       { firstName: "", lastName: "", location: "", occupation: "", picture: "", email: "", password: "" },
  login:          { email: "", password: "" },
  forgotpassword: { email: "" },
  resetpassword:  { password: "", confirmPassword: "" },
};


/* ===== FORM ===== */
const Form = () => {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const { palette } = useTheme();
  const location   = useLocation();

  const [pageType, setPageType]           = useState("login");
  const [image, setImage]                 = useState(null);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [resetToken, setResetToken]       = useState("");
  const [sentEmail, setSentEmail]         = useState(null);
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false,
  });

  // Offline state
  const [isOffline, setIsOffline]           = useState(!navigator.onLine);
  const [offlineCred, setOfflineCred]       = useState(() => getOfflineCredential());
  const [offlinePin, setOfflinePin]         = useState("");
  const [offlinePinLoading, setOfflinePinLoading] = useState(false);
  const [offlinePinError, setOfflinePinError]     = useState("");

  // Set-PIN dialog (after successful online login)
  const [setPinOpen, setSetPinOpen]   = useState(false);
  const [setPinValue, setSetPinValue] = useState("");
  const [setPinError, setSetPinError] = useState("");
  const [setPinLoading, setSetPinLoading] = useState(false);
  const pendingAuthRef = useRef(null); // { user, token, params, ranks }

  const submitLabel = useMemo(() => SUBMIT_LABELS[pageType] ?? "Submit", [pageType]);

  /* ---- Track online/offline ---- */
  useEffect(() => {
    const sync = () => {
      const offline = !navigator.onLine;
      setIsOffline(offline);
      if (!offline) setOfflineCred(getOfflineCredential());
    };

    window.addEventListener("online",  sync);
    window.addEventListener("offline", sync);
    // visibilitychange catches the case where WiFi was toggled while app was in background
    document.addEventListener("visibilitychange", sync);
    // Fallback poll — browser online/offline events are unreliable on some mobile/PWA environments
    const interval = setInterval(sync, 2000);

    return () => {
      window.removeEventListener("online",  sync);
      window.removeEventListener("offline", sync);
      document.removeEventListener("visibilitychange", sync);
      clearInterval(interval);
    };
  }, []);

  /* ---- Complete login after optional PIN setup ---- */
  const proceedAfterPin = useCallback(() => {
    const { user, token, params, ranks } = pendingAuthRef.current;
    dispatch(setLogin({ user, token, params }));
    dispatch(setCrewRanks({ crewRanks: ranks }));
    pendingAuthRef.current = null;
    navigate("/dashboard");
  }, [dispatch, navigate]);

  const handleSetPin = useCallback(async () => {
    if (setPinValue.length < 4) {
      setSetPinError("PIN must be at least 4 digits.");
      return;
    }
    setSetPinLoading(true);
    try {
      const { user, params } = pendingAuthRef.current;
      await saveOfflineCredential(user, params, setPinValue);
      setOfflineCred(getOfflineCredential());
      setSetPinOpen(false);
      proceedAfterPin();
    } catch {
      setSetPinError("Failed to save PIN. Please try again.");
    } finally {
      setSetPinLoading(false);
    }
  }, [setPinValue, proceedAfterPin]);

  const handleSkipPin = useCallback(async () => {
    setSetPinOpen(false);
    const { user, params } = pendingAuthRef.current;
    await saveOfflineCredential(user, params); // no PIN — ensures offline mode is detectable
    setOfflineCred(getOfflineCredential());
    proceedAfterPin();
  }, [proceedAfterPin]);

  /* ---- finalizeLogin — skips dialog if PIN already saved for this crew ---- */
  const finalizeLogin = useCallback(async (token, crewMember) => {
    const authHeader = { headers: { Authorization: `Bearer ${token}` } };
    const [paramsRes, ranksRes] = await Promise.all([
      api.get('/params', authHeader),
      api.get('/ranks/active', authHeader),
    ]);

    // Credential already exists for this crew → refresh silently, skip PIN dialog
    if (refreshOfflineCredential(crewMember, paramsRes.data)) {
      setOfflineCred(getOfflineCredential());
      dispatch(setLogin({ user: crewMember, token, params: paramsRes.data }));
      dispatch(setCrewRanks({ crewRanks: ranksRes.data }));
      navigate("/dashboard");
      return;
    }

    // No credential yet → prompt to set PIN
    pendingAuthRef.current = { user: crewMember, token, params: paramsRes.data, ranks: ranksRes.data };
    setSetPinValue("");
    setSetPinError("");
    setSetPinOpen(true);
  }, [dispatch, navigate]);

  /* ---- Offline PIN login ---- */
  const handleOfflinePinLogin = useCallback(async () => {
    if (!offlinePin || offlinePin.length < 4) {
      setOfflinePinError("Enter your PIN (min 4 digits).");
      return;
    }
    setOfflinePinLoading(true);
    setOfflinePinError("");
    try {
      const ok = await verifyOfflinePin(offlinePin);
      if (ok) {
        const cred = getOfflineCredential();
        dispatch(setOfflineLogin({ user: cred.user, params: cred.params }));
        navigate("/dashboard");
      } else {
        setOfflinePinError("Incorrect PIN. Please try again.");
        setOfflinePin("");
      }
    } catch {
      setOfflinePinError("An error occurred. Please try again.");
    } finally {
      setOfflinePinLoading(false);
    }
  }, [offlinePin, dispatch, navigate]);

  /* ---- Offline biometric login (no server round-trip) ---- */
  const handleOfflineBiometricLogin = useCallback(async () => {
    if (!offlineCred?.biometricCredentialId) return;
    try {
      setBiometricLoading(true);

      // Generate a local challenge — we only need the biometric gate, not server verification
      const challengeBytes = crypto.getRandomValues(new Uint8Array(32));
      const challenge = btoa(String.fromCharCode(...challengeBytes))
        .replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");

      await startAuthentication({
        optionsJSON: {
          challenge,
          timeout: 60000,
          rpId: window.location.hostname,
          allowCredentials: [{
            id: offlineCred.biometricCredentialId,
            type: "public-key",
          }],
          userVerification: "required",
        },
      });

      // Biometric verified locally — use cached offline credentials
      dispatch(setOfflineLogin({ user: offlineCred.user, params: offlineCred.params }));
      navigate("/dashboard");
    } catch (err) {
      if (err.name === "NotAllowedError") {
        toast.error("Biometric cancelled or not recognised.");
      } else {
        toast.error("Biometric authentication failed.");
      }
    } finally {
      setBiometricLoading(false);
    }
  }, [offlineCred, dispatch, navigate]);

  /* ---- OAuth callback handling ---- */
  const handleOAuthLogin = useCallback(async (token, error) => {
    if (error) {
      toast.error(decodeURIComponent(error));
      navigate("/login", { replace: true });
      return;
    }
    if (!token) return;
    try {
      const { data } = await api.get('/crewmembers/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      await finalizeLogin(token, data.crewMember);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to process OAuth2 login!");
      navigate("/login", { replace: true });
    }
  }, [finalizeLogin, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location?.search);
    const token  = params.get("token");
    const error  = params.get("error");
    const rt     = params.get("resetToken");

    if (rt) {
      setResetToken(rt);
      setPageType("resetpassword");
      return;
    }

    if (token || error) {
      handleOAuthLogin(token, error).catch(() => {
        navigate("/login", { replace: true });
      });
    }
  }, [location.search, handleOAuthLogin, navigate]);

  /* ---- Biometric login ---- */
  const handleBiometricLogin = useCallback(async (email) => {
    if (!email) {
      toast.error("Enter email to use biometrics.");
      return;
    }
    try {
      setBiometricLoading(true);
      const { data: options } = await api.post('/crewmembers/webauthn/authentication-options', { email });
      const response = await startAuthentication({ optionsJSON: options });
      const { data } = await api.post('/crewmembers/webauthn/authentication-verify', { email, response });
      if (data.success) {
        // Cache credential ID so biometric can be used offline next time
        saveBiometricCredentialId(response.id);
        await finalizeLogin(data.token, data.crewMember);
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error("Biometric authentication was not available.");
      } else {
        toast.error(err.response?.data?.message || err.message || "Biometric login failed!");
      }
    } finally {
      setBiometricLoading(false);
    }
  }, [finalizeLogin]);

  /* ---- OAuth sign-in ---- */
  const signIn = useCallback((provider) => {
    window.location.href = `${API_URL}/crewmembers/auth/${provider}`;
  }, []);

  /* ---- Password visibility toggle ---- */
  const togglePasswordVisibility = useCallback((key) => {
    setShowPasswords((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  /* ---- Form submit ---- */
  const handleFormSubmit = useCallback(async (values, { resetForm }) => {
    try {
      if (pageType === "register") {
        const formData = new FormData();
        Object.entries(values).forEach(([k, v]) => formData.append(k, v));
        if (image) {
          formData.append("picture", image);
          formData.append("picturePath", image.name);
        }
        await api.post('/crewmembers/register', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setImage(null);
        setPageType("login");
        toast.success("Registration successful. Please login.");

      } else if (pageType === "login") {
        const { data } = await api.post('/crewmembers/login', values);
        if (data.success) {
          await finalizeLogin(data.token, data.crewMember);
        }

      } else if (pageType === "forgotpassword") {
        const { data } = await api.post('/crewmembers/forgotpassword', values);
        if (data.success) {
          setSentEmail(values.email);
        }

      } else if (pageType === "resetpassword") {
        const { data } = await api.post(`/crewmembers/resetpassword/${resetToken}`, values);
        if (data.success) {
          setResetToken("");
          setPageType("login");
          toast.success("Password has been reset.");
        }
      }

      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "An error occurred. Please try again.");
    }
  }, [pageType, image, resetToken, finalizeLogin]);


  return (
    <>
      {/* ===== SET-PIN DIALOG (after online login) ===== */}
      <Dialog open={setPinOpen} maxWidth="xs" fullWidth>
        <DialogTitle>Set Offline PIN</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Set a PIN to log in when you don't have internet access.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            label="PIN (4–6 digits)"
            type="password"
            value={setPinValue}
            onChange={(e) => {
              setSetPinValue(e.target.value.replace(/\D/g, "").slice(0, 6));
              setSetPinError("");
            }}
            onKeyDown={(e) => { if (e.key === "Enter") handleSetPin(); }}
            error={Boolean(setPinError)}
            helperText={setPinError || " "}
            slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 6 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined"
            color="secondary"
            disabled={setPinLoading} 
            onClick={handleSkipPin} 
          >
            Skip
          </Button>
          <Button 
            variant="outlined"
            disabled={setPinLoading} 
            onClick={handleSetPin} 
          >
            {setPinLoading ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== HEADER ROW ===== */}
      <Box display="flex" alignItems="center" mb={3}>
        <Box sx={{ width: 34 }}>
          {(pageType === "forgotpassword" || pageType === "resetpassword") && !isOffline && (
            <IconButton
              size="small"
              onClick={() => { setPageType("login"); setSentEmail(null); }}
              sx={{
                color: palette.text.secondary,
                "&:hover": { color: palette.primary.main },
              }}
            >
              <ArrowBackIosNew fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Typography 
          flex={1} 
          variant="h5" 
          textAlign="center" 
          color="primary"
        >
          ONBOARD SALES
        </Typography>
        <Box sx={{ width: 34 }} />
      </Box>

      {/* ===== OFFLINE + SAVED CREDENTIAL → PIN LOGIN ===== */}
      {isOffline && offlineCred ? (
        <Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={0.75}
            mb={3}
            sx={{ color: "text.secondary" }}
          >
            <WifiOff fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              Offline mode
            </Typography>
          </Box>

          <Typography 
            variant="body2" 
            textAlign="center" 
            color="text.secondary" 
            mb={0.5}
          >
            Welcome back
          </Typography>
          <Typography 
            variant="h6" 
            textAlign="center" 
            fontWeight={600} 
            color="primary" 
            mb={3}
          >
            {offlineCred.crewName || offlineCred.crewCode}
          </Typography>

          {offlineCred.pinHash ? (
            <>
              <TextField
                fullWidth
                label="Offline PIN"
                type="password"
                value={offlinePin}
                autoFocus
                onChange={(e) => {
                  setOfflinePin(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setOfflinePinError("");
                }}
                onKeyDown={(e) => { if (e.key === "Enter") handleOfflinePinLogin(); }}
                error={Boolean(offlinePinError)}
                helperText={offlinePinError || "Enter your PIN to continue offline"}
                slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 6 } }}
                sx={{ mb: 2 }}
              />
              <Box display="flex" alignItems="stretch" gap={1} sx={{ m: "0.5rem 0" }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleOfflinePinLogin}
                  disabled={offlinePinLoading || biometricLoading}
                  sx={{
                    p: "1rem",
                    bgcolor: palette.primary.main,
                    color: palette.getContrastText(palette.primary.main),
                    "&:hover": { bgcolor: palette.primary.dark },
                  }}
                >
                  {offlinePinLoading ? <CircularProgress size={22} /> : "Login Offline"}
                </Button>
                {WEBAUTHN_SUPPORTED && offlineCred?.biometricCredentialId && (
                  <Button
                    onClick={handleOfflineBiometricLogin}
                    disabled={biometricLoading || offlinePinLoading}
                    sx={{
                      borderRadius: 1,
                      px: 2.5,
                      border: "1px solid",
                      borderColor: palette.neutral?.deep ?? palette.divider,
                      color: palette.text.primary,
                      "&:hover": { borderColor: palette.primary.main },
                    }}
                  >
                    {biometricLoading ? <CircularProgress size={22} /> : <Fingerprint />}
                  </Button>
                )}
              </Box>
            </>
          ) : WEBAUTHN_SUPPORTED && offlineCred?.biometricCredentialId ? (
            <Button
              fullWidth
              variant="contained"
              onClick={handleOfflineBiometricLogin}
              disabled={biometricLoading}
              sx={{
                p: "1rem",
                bgcolor: palette.primary.main,
                color: palette.getContrastText(palette.primary.main),
                "&:hover": { bgcolor: palette.primary.dark },
                gap: 1,
              }}
            >
              {biometricLoading ? <CircularProgress size={22} /> : <><Fingerprint /> Biometric Login</>}
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
              No offline PIN set. Connect to the internet to log in.
            </Typography>
          )}

          <Typography
            variant="caption"
            color="text.disabled"
            textAlign="center"
            display="block"
            mt={2}
          >
            Connect to the internet to use your full credentials.
          </Typography>
        </Box>

      ) : (
        /* ===== NORMAL LOGIN FORM ===== */
        <>
          {/* Offline notice when no saved credential */}
          {isOffline && !offlineCred && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={0.75}
              mb={2}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: "warning.main",
                color: "warning.contrastText",
                opacity: 0.9,
              }}
            >
              <WifiOff fontSize="small" />
              <Typography variant="body2">
                You're offline - login requires internet.
              </Typography>
            </Box>
          )}

          {/* Email sent confirmation */}
          {pageType === "forgotpassword" && sentEmail && (
            <Box textAlign="center" py={2}>
              <Typography variant="h6" color="primary" mb={1}>
                Check your email
              </Typography>
              <Typography color="text.secondary" mb={2}>
                We sent a password reset link to <strong>{sentEmail}</strong>.
                The link expires in 1 hour.
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Didn't receive it? Check your spam folder or{" "}
                <Typography
                  component="span"
                  variant="body2"
                  onClick={() => setSentEmail(null)}
                  sx={{
                    color: "primary.main",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  try again
                </Typography>.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => { setPageType("login"); setSentEmail(null); }}
                sx={{ textTransform: "none" }}
              >
                Back to Login
              </Button>
            </Box>
          )}

          {!(pageType === "forgotpassword" && sentEmail) && (
            <Formik
              onSubmit={handleFormSubmit}
              initialValues={initialValues[pageType]}
              validationSchema={validationSchemas[pageType]}
              enableReinitialize
            >
              {({ values, errors, touched, handleBlur, handleChange, handleSubmit, resetForm }) => {
                const field = (name) => ({
                  name,
                  onBlur: handleBlur,
                  onChange: handleChange,
                  value: values[name] ?? "",
                  error: Boolean(touched[name] && errors[name]),
                  helperText: touched[name] && errors[name],
                });

                return (
                  <form onSubmit={handleSubmit}>
                    <Box
                      display="grid"
                      gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                      gap={2}
                      sx={{
                        "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
                          WebkitBoxShadow: `0 0 0 1000px ${palette.background.alt} inset`,
                          WebkitTextFillColor: palette.text.primary,
                        },
                      }}
                    >
                      {/* Register-only fields */}
                      {pageType === "register" && (
                        <React.Fragment>
                          <TextField {...field("firstName")} label="First Name" sx={{ gridColumn: "span 6" }} />
                          <TextField {...field("lastName")} label="Last Name" sx={{ gridColumn: "span 6" }} />
                          <TextField {...field("location")} label="Location" sx={{ gridColumn: "span 12" }} />
                          <TextField {...field("occupation")} label="Occupation" sx={{ gridColumn: "span 12" }} />
                          <Box
                            gridColumn="span 12"
                            borderRadius={1}
                            border={`1px solid ${palette.neutral?.deep ?? palette.divider}`}
                            p={1}
                          >
                            <Dropzone image={image} setImage={setImage} />
                          </Box>
                        </React.Fragment>
                      )}

                      {/* Email - not shown on resetpassword */}
                      {pageType !== "resetpassword" && (
                        <TextField
                          {...field("email")}
                          label="Email"
                          sx={{ gridColumn: "span 12" }}
                        />
                      )}

                      {/* Password - not shown on forgotpassword */}
                      {pageType !== "forgotpassword" && (
                        <TextField
                          {...field("password")}
                          label={pageType === "resetpassword" ? "New Password" : "Password"}
                          type={showPasswords.password ? "text" : "password"}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    disableRipple
                                    onClick={() => togglePasswordVisibility("password")}
                                    sx={{
                                      bgcolor: 'transparent',
                                      '&:hover': { bgcolor: 'transparent' },
                                    }}
                                  >
                                    {showPasswords.password ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            },
                          }}
                          sx={{ gridColumn: "span 12" }}
                        />
                      )}

                      {/* Confirm password - resetpassword only */}
                      {pageType === "resetpassword" && (
                        <TextField
                          {...field("confirmPassword")}
                          label="Confirm New Password"
                          type={showPasswords.confirmPassword ? "text" : "password"}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    disableRipple
                                    onClick={() => togglePasswordVisibility("confirmPassword")}
                                    sx={{
                                      bgcolor: 'transparent',
                                      '&:hover': { bgcolor: 'transparent' },
                                    }}
                                  >
                                    {showPasswords.confirmPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            },
                          }}
                          sx={{ gridColumn: "span 12" }}
                        />
                      )}
                    </Box>

                    {/* Submit + biometric row */}
                    <Box 
                      display="flex" 
                      alignItems="stretch" 
                      gap={1} 
                      sx={{ m: "1rem 0" }}
                    >
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        sx={{
                          p: "1rem",
                          bgcolor: palette.primary.main,
                          color: palette.getContrastText(palette.primary.main),
                          "&:hover": {
                            bgcolor: palette.primary.dark,
                            color: palette.getContrastText(palette.primary.dark ?? palette.primary.main),
                          },
                        }}
                      >
                        {submitLabel}
                      </Button>
                      {pageType === "login" && WEBAUTHN_SUPPORTED && (
                        <Button
                          onClick={() => handleBiometricLogin(values.email)}
                          disabled={biometricLoading}
                          sx={{
                            borderRadius: 1,
                            px: 2.5,
                            textTransform: "none",
                            color: palette.text.primary,
                            border: "1px solid",
                            borderColor: palette.neutral?.deep ?? palette.divider,
                            "&:hover": { borderColor: palette.primary.main },
                          }}
                        >
                          {biometricLoading ? <CircularProgress size={22} /> : <Fingerprint />}
                        </Button>
                      )}
                    </Box>

                    {/* Navigation links */}
                    {pageType === "login" && (
                      <Box display="flex" justifyContent="flex-end">
                        <Typography
                          onClick={() => { setPageType("forgotpassword"); resetForm(); }}
                          sx={{
                            textDecoration: "underline",
                            color: palette.primary.main,
                            cursor: "pointer",
                            "&:hover": { color: palette.neutral?.main ?? palette.text.secondary },
                          }}
                        >
                          Forgot password
                        </Typography>
                      </Box>
                    )}

                    {/* OAuth providers */}
                    {pageType === "login" && (
                      <Box mt={2}>
                        <Box display="flex" alignItems="center" gap={2} mb={1.5}>
                          <Box 
                            flex={1} 
                            height="1px" 
                            bgcolor={palette.neutral?.deep ?? palette.divider} 
                          />
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            or
                          </Typography>
                          <Box 
                            flex={1} 
                            height="1px" 
                            bgcolor={palette.neutral?.deep ?? palette.divider} 
                          />
                        </Box>
                        <Stack spacing={2}>
                          {PROVIDERS.map((provider) => (
                            <Button
                              key={provider.id}
                              fullWidth
                              variant="outlined"
                              onClick={() => signIn(provider.id)}
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                textTransform: "none",
                                color: palette.text.primary,
                                borderColor: palette.neutral?.deep ?? palette.divider,
                                "&:hover": { borderColor: palette.primary.main },
                                gap: 2,
                                p: 1,
                              }}
                            >
                              {provider.icon}
                              <Typography variant="body1" component="span">
                                {provider.name}
                              </Typography>
                            </Button>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </form>
                );
              }}
            </Formik>
          )}
        </>
      )}
    </>
  );
};

export default Form;
